/**
 * NovaCargaCard — orquestra a nova carga pela tela (RF-M2-007):
 * seleção (DropZone) → cálculo de SHA-256 (HashProgress) → revisão (ReviewTable)
 * → confirmação (POST /cargas) → upload em lotes com retomada (UploadProgress via
 * progresso local do upload) → conclusão.
 *
 * Nenhum dado pessoal em console/analytics (RN-M11-001): erros são genéricos.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { useServices } from '../contexts/ServicesContext';
import { prepararArquivos } from '../services/fileHash';
import { enviarComRenovacao } from '../services/uploadEmLotes';
import { ArquivoPreparado, UploadInstructionObjeto } from '../services/cargas-types';
import { DropZone } from './DropZone';
import { ArquivoSelecionado } from '../services/pastaArrastada';
import { ProgressBar } from './ProgressBar';
import { ReviewTable } from './ReviewTable';

type Estagio = 'idle' | 'hash' | 'review' | 'upload' | 'interrompido' | 'concluido' | 'erro';

export interface NovaCargaCardProps {
  /** Chamado ao concluir uma carga, para a lista recarregar. */
  readonly onConcluido?: () => void;
}

export function NovaCargaCard({ onConcluido }: NovaCargaCardProps): JSX.Element {
  const { cargas } = useServices();
  const [estagio, setEstagio] = useState<Estagio>('idle');
  const [hashFeitos, setHashFeitos] = useState(0);
  const [hashTotal, setHashTotal] = useState(0);
  const [arquivos, setArquivos] = useState<ArquivoPreparado[]>([]);
  const [uploadFeitos, setUploadFeitos] = useState(0);
  // Retomada (RNF-02, US-08): a carga criada, as instruções vigentes e o que já
  // subiu sobrevivem a uma falha — "Retomar envio" continua de onde parou.
  const cargaRef = useRef<{ cargaId: string; objetos: readonly UploadInstructionObjeto[] } | null>(null);
  const enviadosRef = useRef<Set<string>>(new Set());

  // Vai o que não ficou fora: nem o inválido (com o motivo) nem o arquivo de sistema (ignorado — ADR-043).
  const enviaveis = useMemo(
    () => arquivos.filter((a) => a.situacao !== 'invalido' && a.situacao !== 'sistema'),
    [arquivos],
  );

  const onArquivos = useCallback(async (files: ArquivoSelecionado[]) => {
    setEstagio('hash');
    setHashTotal(files.length);
    setHashFeitos(0);
    try {
      const preparados = await prepararArquivos(files, (feitos) => setHashFeitos(feitos));
      setArquivos(preparados);
      setEstagio('review');
    } catch {
      setEstagio('erro');
    }
  }, []);

  const enviar = useCallback(async () => {
    const carga = cargaRef.current;
    if (!carga) {
      return;
    }
    setEstagio('upload');
    try {
      const vigentes = await enviarComRenovacao(enviaveis, carga.objetos, {
        jaEnviados: enviadosRef.current,
        // URL expirada: URLs novas só para o que falta (POST /cargas/{id}/upload-urls).
        renovar: async (pendentes) =>
          (await cargas.renovarUrls(carga.cargaId, pendentes.map(paraManifesto))).uploadInstructions.objetos,
        onProgresso: (p) => setUploadFeitos(p.enviados),
      });
      cargaRef.current = { ...carga, objetos: vigentes };
      setEstagio('concluido');
      onConcluido?.();
    } catch {
      // Rede caiu ou falha persistente: o progresso fica guardado para retomar.
      setUploadFeitos(enviadosRef.current.size);
      setEstagio('interrompido');
    }
  }, [enviaveis, cargas, onConcluido]);

  const confirmar = useCallback(async () => {
    setEstagio('upload');
    setUploadFeitos(0);
    try {
      const carga = await cargas.criar('browser', enviaveis.map(paraManifesto));
      cargaRef.current = { cargaId: carga.cargaId, objetos: carga.uploadInstructions.objetos };
      enviadosRef.current = new Set();
    } catch {
      setEstagio('erro');
      return;
    }
    await enviar();
  }, [enviaveis, cargas, enviar]);

  const cancelar = useCallback(() => {
    cargaRef.current = null;
    enviadosRef.current = new Set();
    setEstagio('idle');
    setArquivos([]);
    setHashFeitos(0);
    setHashTotal(0);
    setUploadFeitos(0);
  }, []);

  return (
    <section className="card">
      <h2>Nova carga pela tela</h2>

      {estagio === 'idle' && <DropZone onArquivos={onArquivos} />}

      {estagio === 'hash' && (
        <ProgressBar feitos={hashFeitos} total={hashTotal} rotulo="Calculando SHA-256" />
      )}

      {estagio === 'review' && (
        <div>
          {/* Spec ajustes-telas-amostra (Req 1): os botões antes da lista — a pasta grande não empurra o "Confirmar". */}
          <div className="row-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={confirmar}
              disabled={enviaveis.length === 0}
            >
              Confirmar carga
            </button>
            <button type="button" className="btn btn-ghost" onClick={cancelar}>
              Cancelar
            </button>
          </div>
          <ReviewTable arquivos={arquivos} />
        </div>
      )}

      {estagio === 'upload' && (
        <ProgressBar feitos={uploadFeitos} total={enviaveis.length} rotulo="Enviando arquivos" />
      )}

      {estagio === 'interrompido' && (
        <div>
          <p className="notice">
            Envio interrompido: {uploadFeitos} de {enviaveis.length} arquivos já estão no acervo. Retome para
            enviar só o que falta.
          </p>
          <div className="row-actions">
            <button type="button" className="btn btn-primary" onClick={enviar}>
              Retomar envio
            </button>
            <button type="button" className="btn btn-ghost" onClick={cancelar}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {estagio === 'concluido' && (
        <div>
          <p className="notice notice-info">
            Carga enviada. A conferência e o relatório aparecem na lista abaixo.
          </p>
          <button type="button" className="btn btn-ghost" onClick={cancelar}>
            Nova carga
          </button>
        </div>
      )}

      {estagio === 'erro' && (
        <div>
          <p className="notice">Ocorreu um erro ao processar a carga. Tente novamente.</p>
          <button type="button" className="btn btn-ghost" onClick={cancelar}>
            Recomeçar
          </button>
        </div>
      )}
    </section>
  );
}

/** Item do manifesto (o mesmo formato do POST /cargas). */
function paraManifesto(a: ArquivoPreparado): { caminho: string; tamanho: number; sha256: string } {
  return { caminho: a.caminho, tamanho: a.tamanho, sha256: a.sha256 };
}

